import {
	Component,
	OnInit,
	QueryList,
	OnDestroy,
	ViewChildren,
} from '@angular/core';
import { FormBuilder, Validators, FormControlName, FormGroup } from '@angular/forms';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { CustomValidators } from 'src/shared/validators/custom-validators';
import { FormConfig } from 'src/shared/form-models/form-cva.component';
import * as CustomerUserSelectors from '../+state/customer-users.selectors';
import * as CustomerUserActions from '../+state/customer-users.actions';
import { CustomerUsersPartialState } from '../+state/customer-users.reducer';
import { Store, select } from '@ngrx/store';
import { SelfDestruct } from 'src/shared/utilities/self-destruct';
import { preventFlicker } from 'src/shared/utilities/custom-rxjs-operators';
import { collectTouchableChildControls } from 'src/shared/utilities/utility-functions';
import { User } from 'src/shared/api';

const customerUserFormConfig: Partial<FormConfig<User>> = {
	id: [''],
	userRegistrationRequestId: [''],
	loginName: [''],
	firstName: ['', Validators.required],
	lastName: ['', Validators.required],
	email: ['', [Validators.required, Validators.email]],
	phoneNumber: ['', Validators.required],
	customer: ['', CustomValidators.hasValidId],
	roles: [''],
	isActive: ['']
};

@Component({
	templateUrl: './customer-user-detail.component.html',
})
export class CustomerUserDetailComponent
	extends SelfDestruct
	implements OnInit, OnDestroy {
	@ViewChildren(FormControlName) childControls: QueryList<FormControlName>;

	private _invalidSubmission = new Subject<number>();
	form = this.fb.group(customerUserFormConfig) as FormGroup;

	readonly id$ = this.store.pipe(
		select(CustomerUserSelectors.getSelectedCustomerUsersId)
	);

	readonly title$ = this.store.pipe(select(CustomerUserSelectors.getTitle));

	readonly isNewRecord$ = this.id$.pipe(filter((id) => !!id));

	readonly isLoading$ = this.store.pipe(
		select(CustomerUserSelectors.getDetailLoadingStatus)
	);

	readonly isFormInvalid$ = this.store.pipe(
		select(CustomerUserSelectors.getError)
	);

	readonly isSaving$ = this.store.pipe(
		select(CustomerUserSelectors.getIsSaving),
		preventFlicker(1000)
	);

	readonly invalidSubmission$ = this._invalidSubmission.asObservable();

	constructor(
		private readonly store: Store<CustomerUsersPartialState>,
		private readonly fb: FormBuilder
	) {
		super();
	}

	ngOnInit() {
		this.store
			.pipe(
				select(CustomerUserSelectors.getSelectedCustomerUser),
				filter<User>(Boolean),
				takeUntil(this.destroyed)
			)
			.subscribe((user) => {
				const {
					id = 0,
					companyName = '',
					address,
					flairDocsCustomerId,
				} = user?.customer ?? {};

				this.form.patchValue(
					{
						...user,
						customer: {
							id,
							name: companyName,
							address,
							flairDocsCustomerId,
						},
					},
					{ emitEvent: false }
				);

				this.form.get('roles').disable();
			});
	}

	save(stay = false): void {
		if (this.form.valid) {
			const formValue = this.form.getRawValue();
			this.store.dispatch(
				CustomerUserActions.saveRequested({
					shouldStayOnPage: stay,
					formValue,
				})
			);
		} else {
			this.highlightInvalidFields();
			this._invalidSubmission.next(new Date().getTime());
		}
	}

	private highlightInvalidFields(): void {
		const controls = collectTouchableChildControls(this.childControls);
		controls.forEach((c) => c.onTouched());
	}

	ngOnDestroy() {
		this.store.dispatch(CustomerUserActions.customerUserDetailDestroyed());
		super.ngOnDestroy();
	}
}
